import * as React from 'react';
import { Field } from 'formik';
import { Grid } from '@mui/material';

import { ReactComponent as CashAndMarketables } from '../icons/CashAndMarketables.svg';
import { ReactComponent as AccountsReceivables } from '../icons/AccountsReceivable.svg';
import { ReactComponent as Inventory } from '../icons/Inventory.svg';
import { ReactComponent as FurnitureFixtures } from '../icons/FurnitureFixtures.svg';
import { ReactComponent as MachineryEquipment } from '../icons/MachineryEquipment.svg';
import { ReactComponent as RollingStockVehicles } from '../icons/RollingStockVehicles.svg';
import { ReactComponent as MarineVessels } from '../icons/MarineVessels.svg';
import { ReactComponent as Helicopters } from '../icons/Helicopters.svg';
import { ReactComponent as FixedAircraft } from '../icons/FixedWingAircraft.svg';
import { ReactComponent as RoyaltyLicensingAgreements } from '../icons/RoyaltyLicensingAgreements.svg';
import { ReactComponent as IntellectualProperty } from '../icons/IntellectualProperty.svg';
import { ReactComponent as OwnerOccupiedRealEstate } from '../icons/OwnerOccupiedRealEstate.svg';

import { CerebroLabel, CerebroMaskedInput } from '.';
import { FormikUtils, NumberUtils, NotNull } from '../utils';
import {
	DealFinancialMetricType,
	DealFinancialMetricTypeMap,
	DealRequestAllocation,
	DealFinancialMetric,
	AnalysisMetricTypes,
	ProfileRealEstateTypeMap,
	ProfileRealEstateType,
} from '../models';
import { CerebroDropdown, CerebroDropdownMenuItem } from './CerebroDropdown';
import { MaxCurrencyLength } from '../utils/platform';

export interface CollateralDetailsItemProps {
	type: DealFinancialMetricType;
	disabled: boolean;
}

export function CollateralDetailsItem({ type, disabled }: CollateralDetailsItemProps) {
	const Icon = GetIconForMetricType(type);

	return (
		<React.Fragment>
			<Field name={BuildCollateralDetailsFormKey(type, 'guid')} type="hidden" />
			<Grid item xs={12} md={6}>
				<CerebroLabel
					htmlFor={BuildCollateralDetailsFormKey(type, 'ttm')}
					sx={{ display: 'flex', alignItems: 'center' }}
				>
					{Icon && <Icon height={25} />}
					{DealFinancialMetricTypeMap[type]}
				</CerebroLabel>
				<Field
					disabled={disabled}
					formatas="currency"
					as={CerebroMaskedInput}
					maxLength={MaxCurrencyLength}
					calculator="true"
					id={BuildCollateralDetailsFormKey(type, 'ttm')}
					placeholder="$0"
					name={BuildCollateralDetailsFormKey(type, 'ttm')}
				/>
			</Grid>
			{type === 'RealEstate' && (
				<Grid item xs={12} md={6}>
					<CerebroLabel
						htmlFor={BuildCollateralDetailsFormKey(type, 'otherDetails')}
						sx={{ display: 'flex', alignItems: 'center' }}
					>
						<span style={{ display: 'inline-block', height: 25 }} />
					</CerebroLabel>
					<Field
						disabled={disabled}
						as={CerebroDropdown}
						id={BuildCollateralDetailsFormKey(type, 'otherDetails')}
						name={BuildCollateralDetailsFormKey(type, 'otherDetails')}
					>
						{Object.entries(ProfileRealEstateTypeMap).map(([value, description]) => (
							<CerebroDropdownMenuItem key={value} value={value}>
								{description}
							</CerebroDropdownMenuItem>
						))}
					</Field>
				</Grid>
			)}
		</React.Fragment>
	);
}

export function GetCollateralTypesForDetails(
	values: DealRequestAllocation | undefined,
	type: AnalysisMetricTypes
): DealFinancialMetricType[] {
	if (values == null) return [];

	var items: DealFinancialMetricType[] = [];

	if (type == 'Borrower') {
		// Has to be first so that is other details is next to it.
		if (values.hasOwnerOccupiedRealEstate) items.push('RealEstate');
		if (values.hasAccountsReceivables) items.push('AccountsReceivables');
		if (values.hasCashMarketables) items.push('Cash');
		if (values.hasInventory) items.push('Inventory');
		if (values.hasFurnitureFixtures) items.push('FurnitureFixture');
		if (values.hasMachineryEquipment) items.push('Equipment');
		if (values.hasRollingStock) items.push('Vehicles');
		if (values.hasMarineVessels) items.push('MarineVessels');
		if (values.hasHelicopters) items.push('Helicopters');
		if (values.hasFixedWingAircraft) items.push('FixedWingAircraft');
		if (values.hasRoyaltyLicensingAgreements) items.push('Royalties');
		if (values.hasIntellectualProperty) items.push('IntellectualProperty');
	}

	if (type == 'Acquisition') {
		// Has to be first so that is other details is next to it.
		if (values.acquisitionHasOwnerOccupiedRealEstate) items.push('RealEstate');

		if (values.acquisitionHasAccountsReceivables) items.push('AccountsReceivables');
		if (values.acquisitionHasCashMarketables) items.push('Cash');
		if (values.acquisitionHasInventory) items.push('Inventory');
		if (values.acquisitionHasFurnitureFixtures) items.push('FurnitureFixture');
		if (values.acquisitionHasMachineryEquipment) items.push('Equipment');
		if (values.acquisitionHasRollingStock) items.push('Vehicles');
		if (values.acquisitionHasMarineVessels) items.push('MarineVessels');
		if (values.acquisitionHasHelicopters) items.push('Helicopters');
		if (values.acquisitionHasFixedWingAircraft) items.push('FixedWingAircraft');
		if (values.acquisitionHasRoyaltyLicensingAgreements) items.push('Royalties');
		if (values.acquisitionHasIntellectualProperty) items.push('IntellectualProperty');
	}

	return items;
}

export function HasSelectedCollateralTypes(values?: DealRequestAllocation) {
	if (values == null) return false;

	return (
		values.hasAccountsReceivables ||
		values.hasCashMarketables ||
		values.hasInventory ||
		values.hasOwnerOccupiedRealEstate ||
		values.hasFurnitureFixtures ||
		values.hasMachineryEquipment ||
		values.hasRollingStock ||
		values.hasMarineVessels ||
		values.hasHelicopters ||
		values.hasFixedWingAircraft ||
		values.hasRoyaltyLicensingAgreements ||
		values.hasIntellectualProperty
	);
}

export function HasSelectedAcquisitionCollateralTypes(values?: DealRequestAllocation) {
	if (values == null) return false;

	return (
		values.acquisitionHasAccountsReceivables ||
		values.acquisitionHasCashMarketables ||
		values.acquisitionHasInventory ||
		values.acquisitionHasOwnerOccupiedRealEstate ||
		values.acquisitionHasFurnitureFixtures ||
		values.acquisitionHasMachineryEquipment ||
		values.acquisitionHasRollingStock ||
		values.acquisitionHasMarineVessels ||
		values.acquisitionHasHelicopters ||
		values.acquisitionHasFixedWingAircraft ||
		values.acquisitionHasRoyaltyLicensingAgreements ||
		values.acquisitionHasIntellectualProperty
	);
}

export function GetIconForMetricType(metricType: DealFinancialMetricType) {
	switch (metricType) {
		case 'AccountsReceivables':
			return AccountsReceivables;
		case 'Cash':
			return CashAndMarketables;
		case 'Inventory':
			return Inventory;
		case 'FurnitureFixture':
			return FurnitureFixtures;
		case 'Equipment':
			return MachineryEquipment;
		case 'Vehicles':
			return RollingStockVehicles;
		case 'MarineVessels':
			return MarineVessels;
		case 'Helicopters':
			return Helicopters;
		case 'FixedWingAircraft':
			return FixedAircraft;
		case 'Royalties':
			return RoyaltyLicensingAgreements;
		case 'IntellectualProperty':
			return IntellectualProperty;
		case 'RealEstate':
			return OwnerOccupiedRealEstate;

		default:
			return null;
	}
}

type CollateralDetailProperties = 'guid' | 'ttm' | 'otherDetails';

type CollateralDetailFormValues = { collateralNotes: string } & {
	[key in CollateralDetailProperties as `${DealFinancialMetricType}|${key}`]: string;
};

export const BuildCollateralDetailsFormKey = (
	type: DealFinancialMetricType,
	property: CollateralDetailProperties
): keyof CollateralDetailFormValues => `${type}|${property}`;

export type CollateralDetailsMutationVariables = FormikUtils.MutateOnSubmitVariables<CollateralDetailFormValues>;

export function ConvertFormValuesToPatches(
	metricTypes: DealFinancialMetricType[],
	formValues: CollateralDetailFormValues,
	analysisMetricType: AnalysisMetricTypes
): Partial<DealFinancialMetric>[] {
	const patches = metricTypes
		.map<Partial<DealFinancialMetric | null>>(metricType => {
			const guid = formValues[BuildCollateralDetailsFormKey(metricType, 'guid')];
			const ttm = NumberUtils.ParseDecimal(
				formValues[BuildCollateralDetailsFormKey(metricType, 'ttm')],
				undefined
			);

			const patch: Partial<DealFinancialMetric> = {
				guid,
				financialMetricType: metricType,
				trailingTwelveMonths: ttm,
				analysisMetricType,
			};

			if (patch.guid === '') delete patch.guid;

			if (metricType === 'RealEstate')
				patch.profileRealEstateType = formValues[
					BuildCollateralDetailsFormKey(metricType, 'otherDetails')
				] as ProfileRealEstateType;

			return patch;
		})
		.filter(NotNull);

	return patches;
}

export function BuildCollateralDetailsFormValues(
	metrics: DealFinancialMetric[],
	allocation: DealRequestAllocation,
	collateralNotesField: 'borrower' | 'acquisition',
	metricTypes: DealFinancialMetricType[]
): CollateralDetailFormValues {
	const formValues = {
		collateralNotes: allocation[`${collateralNotesField}CollateralNotes`] ?? '',

		...(Object.fromEntries(
			metricTypes.flatMap(metricType => [
				[BuildCollateralDetailsFormKey(metricType, 'guid'), ''],
				[BuildCollateralDetailsFormKey(metricType, 'ttm'), ''],
				[BuildCollateralDetailsFormKey(metricType, 'otherDetails'), ''],
			])
		) as Omit<CollateralDetailFormValues, 'collateralNotes'>),
	};

	metrics.forEach(metric => {
		if (metric.financialMetricType == null) return [];

		formValues[BuildCollateralDetailsFormKey(metric.financialMetricType, 'guid')] = metric.guid;

		formValues[BuildCollateralDetailsFormKey(metric.financialMetricType, 'ttm')] = NumberUtils.FormatCurrency(
			metric.trailingTwelveMonths
		);

		if (metric.financialMetricType === 'RealEstate')
			formValues[BuildCollateralDetailsFormKey(metric.financialMetricType, 'otherDetails')] =
				metric.profileRealEstateType ?? 'None';
	});

	return formValues;
}
